import getFromCdn from '@anm/helpers/getFromCdn';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { getLinkTarget } from '@anm/helpers/upgradeLink';
import useVideoSizes from '@anm/hooks/useVideoSizes';
import HostingPlayer, { VideoData, VideoSizes, VideoStatusCode } from '@anm/hosting-player';
import { FC, RefObject } from 'react';
import { useSelector } from 'react-redux';

import { PASSWORD_TAB } from '../../../../../pages/video';
import vtConfig from '../../../../../config';
import { hostedVideoSelectors } from '../../../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../../../store/modules/project';
import { LoaderWrapper } from '../../Wrapper';

import { NoVideoBadge, Placeholder, PlayerContainer } from './Wrapper';

export * from './Wrapper';

type PlayerBlockProps = {
  sizes: VideoSizes;
  hostId: string;
  videoData: VideoData | null;
  container?: RefObject<HTMLDivElement>;
  fullWidth?: boolean;
  errorCode?: VideoStatusCode;
  projectId?: string;
  playerSizes?: VideoSizes;
  activeButton?: number;
  errorMessage?: string;
  onInit?: () => void;
  setCurrentTime?: (time: number) => void;
};

type NoVideoMessageProps = { projectId: string };

const PlayerViewContainer: FC<{ disabled: boolean }> = ({ disabled, children }) => (
  <>{disabled ? <Placeholder>{children}</Placeholder> : <>{children}</>}</>
);

export const NoVideoMessage: FC<NoVideoMessageProps> = ({ projectId }) => {
  const editorUrl = `${vtConfig().urls.editor}/#p=${projectId}&target=publish`; //  TODO:change to project id

  return !isMobileOrIPad() ? (
    <>
      This video has not been published yet.{' '}
      <a href={editorUrl} target={getLinkTarget()}>
        Publish it in the editor
      </a>{' '}
      to see the preview here.
    </>
  ) : (
    <>This video has not been published yet. Switch to desktop to publish this video.</>
  );
};

const PlayerBlock: FC<PlayerBlockProps> = ({
  sizes,
  hostId,
  projectId,
  videoData,
  errorCode,
  container,
  fullWidth,
  errorMessage,
  activeButton,
  playerSizes: playerSizesFromProps,
  onInit,
  setCurrentTime
}) => {
  const playerSizes = useVideoSizes({ container, sizes, fullWidth }) || playerSizesFromProps;
  const isPasswordScreen = activeButton === PASSWORD_TAB;
  const password = useSelector(hostedVideoSelectors.selectVideoPassword);
  const isNoVideo = errorCode === VideoStatusCode.no_video;
  const canShowNoVideoBadge = !!(isNoVideo && !isPasswordScreen && projectId);
  const preview = useSelector(projectSelectors.selectCurrentProjectDefaultPreview);

  const videoPoster = isNoVideo ? preview : videoData?.poster.posterUrl;

  if (!playerSizes) return <LoaderWrapper size="large" color="blue" />;

  return (
    <PlayerContainer {...playerSizes} isPasswordScreen={isPasswordScreen}>
      {canShowNoVideoBadge && (
        <NoVideoBadge variant="orange" icon={getFromCdn('images/icons/notification/warning.svg')}>
          <NoVideoMessage projectId={projectId!} />
        </NoVideoBadge>
      )}

      <PlayerViewContainer disabled={isPasswordScreen}>
        <HostingPlayer
          videoId={hostId}
          {...{ password, onInit }}
          target="customization"
          parentVideoData={{
            ...(!isPasswordScreen && { errorMessage }),
            videoData: {
              ...videoData!,
              ...(videoPoster && { poster: { posterUrl: videoPoster } }),
              sizes
            },
            errorCode: isPasswordScreen ? VideoStatusCode.protected : errorCode
          }}
          onTimeChange={setCurrentTime}
          playerWidth={playerSizes?.width}
        />
      </PlayerViewContainer>
    </PlayerContainer>
  );
};

export default PlayerBlock;
